import { clsx } from 'clsx';
import type { ReactNode } from 'react';

export interface Delta {
  value: ReactNode;
  up?: boolean;
}

export interface StatCardProps {
  label: string;
  value: ReactNode;
  delta?: Delta;
  sparkline?: number[];
  className?: string;
}

function buildSparkPath(data: number[], width: number, height: number): { line: string; fill: string } {
  if (!data.length) return { line: '', fill: '' };
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = data.length > 1 ? width / (data.length - 1) : 0;
  const points = data.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * height;
    return [x, y];
  });
  const line = points.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(' ');
  const fill = `${line} L${width},${height} L0,${height} Z`;
  return { line, fill };
}

export function StatCard({ label, value, delta, sparkline, className }: StatCardProps) {
  const w = 280;
  const h = 32;
  const sp = sparkline ? buildSparkPath(sparkline, w, h) : null;

  return (
    <div
      className={clsx(
        'rounded-xl border border-line bg-bg-card p-4',
        className,
      )}
    >
      <div className="text-[10px] font-bold uppercase tracking-label text-fg-muted">{label}</div>
      <div className="mt-1.5 text-2xl font-bold tracking-heading">{value}</div>
      {delta && (
        <div
          className={clsx(
            'mt-1 text-xs font-semibold',
            delta.up ? 'text-accent' : 'text-fg-secondary',
          )}
        >
          {delta.up ? '▲' : '▼'} {delta.value}
        </div>
      )}
      {sp && (
        <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="mt-2 w-full h-8">
          <path d={sp.fill} fill="#bef264" opacity={0.12} />
          <path d={sp.line} fill="none" stroke="#bef264" strokeWidth={2} />
        </svg>
      )}
    </div>
  );
}
